import { useEffect, useRef, useState } from 'react';
import { fetchClient } from '@/lib/apiClient';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

const AUTOCOMPLETE_LIMIT = 8;

export interface LocationSearchResult {
  slug: string;
  name: string;
  postcode?: string;
  /**
   * `Locations.shNumber`, returned by `GET /locations/search`. The tutor
   * application form submits these as `schoolNumbers`, so a Standort without
   * one cannot be applied to.
   */
  centerNumber?: number;
}

function isCitySearch(query: string): boolean {
  return /[^\d]/.test(query);
}

export function meetsThreshold(query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return false;
  }
  return isCitySearch(trimmed) ? trimmed.length >= 3 : trimmed.length >= 2;
}

export function useLocationSearch(query: string) {
  const { domain } = useRuntimeConfig();
  const [results, setResults] = useState<LocationSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortRef = useRef<AbortController>(undefined);

  useEffect(() => {
    const trimmed = query.trim();

    if (!meetsThreshold(trimmed)) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const { data } = await fetchClient.GET('/locations/search', {
          params: {
            query: {
              searchTerm: trimmed,
              limit: AUTOCOMPLETE_LIMIT,
              // Scopes the hits to this brand. Austria and Switzerland both use
              // four-digit postcodes and overlap, so without it `6300` offers an
              // Austrian visitor a Swiss Standort. From the runtime config, not
              // `domain-config`: `BRAND` is server-only and a client-side read
              // would silently answer `de` (70.13). It travels as a query
              // parameter rather than a header because this request goes through
              // the CDN, where only the URL is part of the cache key.
              domain,
            },
          },
          signal: controller.signal,
        });

        setResults(data ?? []);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 200);

    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [query, domain]);

  return { results, isLoading };
}
